import c from 'classnames';
import React from 'react';

import { TextColor, Typography } from '@repo/ui';
import useGutType from 'services/gutType/useGutType';

import { Kit, TinyAccount } from '@repo/types';
import { AgeGroup, getAgeGroup } from '@repo/utils';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { KitType } from 'types/Kit';
import { getGutType } from '../helpers';
import { GutTypeValue } from '../helpers/gutTypes';
import { getVagType } from '../helpers/vagTypes';
import styles from './MicrobiomeType.module.css';
import slideStyles from './Slides.module.css';

function getTypeConfig(
  kit: Kit,
  account: TinyAccount,
  normalizedGutType: string,
): { typeData: GutTypeValue; title: string } {
  if (kit.sampling_type === KitType.VAGINAL) {
    return {
      typeData: getVagType(normalizedGutType),
      title: 'Your Vaginal Community State Type',
    };
  }

  const age = kit.age_at_sampling ?? 0;
  const isAdult = getAgeGroup(age) === AgeGroup.ADULT;
  return {
    typeData: getGutType(normalizedGutType, age),
    title: isAdult ? 'Your gut type' : `${account.first_name}'s gut type`,
  };
}

export const MicrobiomeType: React.FC = () => {
  const { currentKit, currentTinyAccount } = useCurrentKitAndTinyAccount();
  const { gutType, data } = useGutType();
  const gutTypeData = data?.gut_vaginal_type;

  if (!currentKit || !currentTinyAccount) {
    return <></>;
  }

  const normalizedGutType = gutType?.toLowerCase() as string;
  const { typeData: hardcodedTypeData, title } = getTypeConfig(
    currentKit,
    currentTinyAccount,
    normalizedGutType,
  );

  const typeName =
    currentKit.sampling_type === KitType.VAGINAL
      ? `Type ${hardcodedTypeData.type}`
      : hardcodedTypeData.type;

  return (
    <div className={c(slideStyles.slideWrapper, styles.slideContainer)}>
      <div
        className={c(
          styles.background,
          styles[`microbiome-type-${normalizedGutType}`],
          hardcodedTypeData.extraBackgroundClass &&
            styles[hardcodedTypeData.extraBackgroundClass],
        )}
      />
      <div className={styles.header}>
        <Typography variant='heading-l'>{title}</Typography>
        <Typography variant='heading-2xl'>{typeName}</Typography>
      </div>
      <img
        src={hardcodedTypeData.characterImage}
        alt={hardcodedTypeData.type}
        className={c(
          styles.character,
          currentKit.sampling_type === KitType.STOOL && styles['character-gut'],
        )}
      />
      <div className={styles.textContainer}>
        <Typography
          variant='body-m'
          color={TextColor.DARKGREEN}
          weight='medium'
        >
          {gutTypeData?.subtitle ?? hardcodedTypeData.shortDescription}
        </Typography>
        <Typography variant='body-m'>
          {gutTypeData?.short_explanation ?? hardcodedTypeData.longDescription}
        </Typography>
      </div>
    </div>
  );
};
